// @flow

import { useCallback, useEffect, useRef, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'

import { useAnalyticsTrack } from './useAnalyticsTrack'
import { stripUrlQuery } from '../utils/stripUrlQuery'

const DEFAULT_INTERVAL_MS = 60000

export type SessionContent = {
  contentType: string,
  contentId: string,
  contentTitle: string,
}

export type UseSessionHeartbeatOptions = {|
  eventName: string,
  contentType: string,
  contentId: string,
  contentTitle: string,
  isActive?: boolean,
  buildExtraPayload: (content: SessionContent) => ?Object,
  onSessionStart?: () => void,
  intervalMs?: number,
|}

export type UseSessionHeartbeatReturn = {|
  onSourceLoaded: (src: string) => void,
  onActivePlayingChange: (isActivePlaying: boolean) => void,
  onEnd: () => void,
|}

export const useSessionHeartbeat = ({
  eventName,
  contentType,
  contentId,
  contentTitle,
  isActive = true,
  buildExtraPayload,
  onSessionStart,
  intervalMs = DEFAULT_INTERVAL_MS,
}: UseSessionHeartbeatOptions): UseSessionHeartbeatReturn => {
  const track = useAnalyticsTrack()

  const [isActivePlaying, setIsActivePlaying] = useState(false)
  const [isHidden, setIsHidden] = useState(false)

  const isSessionActive = Boolean(contentId) && isActive

  const trackRef = useRef(track)
  trackRef.current = track

  const buildExtraRef = useRef(buildExtraPayload)
  buildExtraRef.current = buildExtraPayload

  const onSessionStartRef = useRef(onSessionStart)
  onSessionStartRef.current = onSessionStart

  const sessionIdRef = useRef('')
  const sessionContentRef = useRef({ contentType: '', contentId: '', contentTitle: '' })
  const loadedUrlRef = useRef('')
  const accumulatedMsRef = useRef(0)
  const playingSinceRef = useRef<number | null>(null)
  const timeoutIdRef = useRef<TimeoutID | null>(null)

  const elapsedSeconds = () => {
    const startedAt = playingSinceRef.current
    const liveMs = startedAt === null ? 0 : Date.now() - startedAt

    return Math.floor((accumulatedMsRef.current + liveMs) / 1000)
  }

  const cancelScheduled = () => {
    if (timeoutIdRef.current !== null) {
      clearTimeout(timeoutIdRef.current)

      timeoutIdRef.current = null
    }
  }

  // eslint-disable-next-line complexity
  const flush = () => {
    const total = elapsedSeconds()

    if (total <= 0) {
      return
    }

    const content = sessionContentRef.current
    const extra = content.contentId ? buildExtraRef.current(content) : null

    if (extra) {
      trackRef.current(eventName, {
        content_type: content.contentType,
        content_id: content.contentId,
        content_title: content.contentTitle,
        content_url: loadedUrlRef.current,
        ...extra,
        session_id: sessionIdRef.current,
        session_duration: total,
      })
    }

    accumulatedMsRef.current = 0

    if (playingSinceRef.current !== null) {
      playingSinceRef.current = Date.now()
    }
  }

  const scheduleNextTick = () => {
    const remainingMs = Math.max(0, intervalMs - accumulatedMsRef.current)

    timeoutIdRef.current = setTimeout(() => {
      flush()
      scheduleNextTick()
    }, remainingMs)
  }

  useEffect(() => {
    if (!isSessionActive) {
      return undefined
    }

    sessionIdRef.current = uuidv4()
    sessionContentRef.current = { contentType, contentId, contentTitle }
    accumulatedMsRef.current = 0
    playingSinceRef.current = null

    if (onSessionStartRef.current) {
      onSessionStartRef.current()
    }

    return () => {
      cancelScheduled()
      flush()

      accumulatedMsRef.current = 0
      playingSinceRef.current = null
    }
  }, [contentType, contentId, isActive]) // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line complexity
  useEffect(() => {
    if (!isSessionActive || !isActivePlaying || isHidden) {
      return undefined
    }

    playingSinceRef.current = Date.now()

    scheduleNextTick()

    return () => {
      cancelScheduled()

      const startedAt = playingSinceRef.current

      if (startedAt !== null) {
        accumulatedMsRef.current += Date.now() - startedAt
        playingSinceRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActivePlaying, isHidden, isSessionActive, contentId, contentType, intervalMs])

  useEffect(() => {
    const handleVisibility = () => setIsHidden(document.hidden)

    document.addEventListener('visibilitychange', handleVisibility)

    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [])

  const onEnd = useCallback(() => {
    cancelScheduled()
    flush()

    accumulatedMsRef.current = 0
    sessionIdRef.current = uuidv4()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const onSourceLoaded = useCallback((src: string) => {
    loadedUrlRef.current = stripUrlQuery(src)
  }, [])

  return {
    onActivePlayingChange: setIsActivePlaying,
    onEnd,
    onSourceLoaded,
  }
}

export default useSessionHeartbeat
